import React, { useState } from 'react';
import { notification, Spin } from 'antd';
import styled from 'styled-components';

import ButtonNext from '@domain/button-next';
import { useVismaConfiguration } from '@services/visitor-management';

import type {
  ElearningWpRemoteTestAnswer,
  ElearningWpRemoteTestQuestion,
  ElearningWpRemoteTestSubmitResponse
} from '../../services/elearning-wp-remote';

const Wrapper = styled.div`
  width: 100%;
  margin-top: 32px;
  border-top: 1px solid ${({ theme }) => theme.border};
  padding-top: 24px;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  margin-bottom: 8px;

  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
  }
`;

const Description = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: ${({ theme }) => theme.textSecondary};
  margin-bottom: 24px;
  white-space: pre-wrap;

  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
  }
`;

const QuestionBlock = styled.div`
  margin-bottom: 24px;
`;

const QuestionText = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  margin-bottom: 12px;

  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: 10px;

  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
    & > input {
      min-width: 20px;
    }
  }
`;

const Radio = styled.input`
  width: 1.5em;
  height: 1.5em;
  accent-color: ${({ theme }) => theme.primary};
  margin-right: 0.75em;
  flex-shrink: 0;
`;

const AnswerText = styled.span`
  font-size: 15px;
  font-weight: 400;
  color: ${({ theme }) => theme.textSecondary};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
  margin-bottom: 40px;

  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
  }
`;

export interface TestAnswer {
  questionId: number;
  answerToken: string;
}

interface ElearningTestFeatureProps {
  questions: ElearningWpRemoteTestQuestion[];
  isLoading: boolean;
  onSubmit: (testAnswers: TestAnswer[]) => Promise<unknown>;
  onNext: () => Promise<unknown>;
}

function ElearningTestFeature({
  questions,
  isLoading,
  onSubmit,
  onNext
}: ElearningTestFeatureProps) {
  const { configuration } = useVismaConfiguration();
  const {
    title,
    description,
    button_retry: buttonRetry
  } = configuration?.elearningTestData ?? {};
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [failed, setFailed] = useState(false);

  const questionList = [...questions].sort((a, b) => a.idx - b.idx);
  const allAnswered =
    questionList.length > 0 &&
    questionList.every((q) => answers[q.id] !== undefined);

  const handleSubmit = async () => {
    const testAnswers = Object.entries(answers).map(
      ([questionId, answerToken]) => ({
        questionId: Number(questionId),
        answerToken
      })
    );
    const result = (await onSubmit(
      testAnswers
    )) as ElearningWpRemoteTestSubmitResponse;
    if (result.status === 'success') {
      notification.success({
        title: result.title,
        description: result.message,
        placement: 'topRight'
      });
      await onNext();
    } else {
      notification.error({
        title: result.title,
        description: result.message,
        placement: 'topRight'
      });
      setFailed(true);
      setAnswers({});
    }
  };

  return (
    <Spin spinning={isLoading} size="large">
      <Wrapper>
        {title && <Title>{title}</Title>}
        {description && <Description>{description}</Description>}
        {questionList.map((q, idx) => (
          <QuestionBlock key={q.id}>
            <QuestionText>
              {idx + 1}. {q.question}
            </QuestionText>
            {q.answers.map((a: ElearningWpRemoteTestAnswer) => (
              <RadioGroup key={a.answerToken}>
                <Radio
                  type="radio"
                  checked={answers[q.id] === a.answerToken}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setAnswers((prev) => ({
                        ...prev,
                        [q.id]: a.answerToken
                      }));
                    }
                  }}
                />
                <AnswerText
                  onClick={() =>
                    setAnswers((prev) => ({
                      ...prev,
                      [q.id]: a.answerToken
                    }))
                  }
                >
                  {a.text}
                </AnswerText>
              </RadioGroup>
            ))}
          </QuestionBlock>
        ))}

        <ButtonContainer>
          <ButtonNext
            title={failed && buttonRetry ? buttonRetry : undefined}
            onClick={handleSubmit}
            disabled={!allAnswered}
            isLoading={isLoading}
            withRightArrow
          />
        </ButtonContainer>
      </Wrapper>
    </Spin>
  );
}

export default ElearningTestFeature;
